import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Bot,
  Calendar,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import supervityLogo from '../assets/supervity_favicon.png';

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin } = useAdmin();

  const navItems = [
    {
      to: '/dashboards',
      icon: <LayoutDashboard size={20} />,
      label: t('dashboard.title') || 'Dashboards',
      id: 'nav-dashboards',
    },
    {
      to: '/schedules',
      icon: <Calendar size={20} />,
      label: t('common.schedule') || 'Schedule',
      id: 'nav-schedules',
    },
    {
      to: '/team-settings',
      icon: <Users size={20} />,
      label: t('dashboardPage.manageTeam') || 'Manage Team',
      id: 'nav-team',
    },
    {
      to: '/integrations',
      icon: <Settings size={20} />,
      label: t('integrations.title') || 'Integrations',
      id: 'nav-integrations',
    },
    ...(isAdmin
      ? [
          {
            to: '/agents',
            icon: <Bot size={20} />,
            label: t('agents.sidebarLabel'),
            id: 'nav-agents',
            adminOnly: true,
          },
        ]
      : []),
  ];

  return (
    <aside
      className={`
        relative flex flex-col
        bg-white border-r border-gray-200
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-56'}
        min-h-screen shrink-0
      `}
      style={{ zIndex: 40 }}
    >
      {/* Logo area */}
      <div
        className={`flex items-center gap-2 px-3 py-4 border-b border-gray-100 overflow-hidden`}
        style={{ minHeight: 64 }}
      >
        <img src={supervityLogo} alt="Supervity" className="w-8 h-8 shrink-0" />
        {!collapsed && (
          <span className="text-base font-bold text-gray-800 whitespace-nowrap truncate">
            Supervity
          </span>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-hidden">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              id={item.id}
              title={collapsed ? item.label : undefined}
              className={`
                flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-150 group
                ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
                ${item.adminOnly ? 'border border-indigo-100' : ''}
              `}
            >
              <span
                className={`shrink-0 ${
                  isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'
                } ${item.adminOnly ? 'text-indigo-500' : ''}`}
              >
                {item.icon}
              </span>
              {!collapsed && (
                <span className="truncate whitespace-nowrap">{item.label}</span>
              )}
              {/* Active bar */}
              {isActive && (
                <span className="ml-auto w-1 h-5 rounded-full bg-indigo-500 shrink-0" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="
          absolute -right-3 top-1/2 -translate-y-1/2
          w-6 h-6 rounded-full bg-white border border-gray-200
          flex items-center justify-center
          shadow-sm hover:shadow-md text-gray-400 hover:text-gray-600
          transition-all duration-150
        "
        id="sidebar-collapse-toggle"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
      </button>
    </aside>
  );
};

export default Sidebar;
